import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/shared/ui/card";
import { Badge } from "@/shared/ui/badge";
import { Progress } from "@/shared/ui/progress";
import { Button } from "@/shared/ui/button";
import { Switch } from "@/shared/ui/switch";
import {
  AlertTriangle,
  Brain,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Clock,
  Cpu,
  Eye,
  Factory,
  MapPin,
  Power,
  Rocket,
  Settings,
} from "lucide-react";
import type { Area, Line, Station, StationDeployment } from "../types";

interface DeploymentTabProps {
  areas: Area[];
  lines: Line[];
  stations: Station[];
  expandedAreas: Set<string>;
  expandedLines: Set<string>;
  toggleArea: (areaId: string) => void;
  toggleLine: (lineId: string) => void;
  getStationDeployment: (stationName: string, index: number) => StationDeployment | null;
  stationHasAIMappings: (stationId: string) => boolean;
  getStationMappingCount: (stationId: string) => number;
  setSelectedStation: (station: Station) => void;
  showMockData: boolean;
}

export function DeploymentTab({
  areas,
  lines,
  stations,
  expandedAreas,
  expandedLines,
  toggleArea,
  toggleLine,
  getStationDeployment,
  stationHasAIMappings,
  getStationMappingCount,
  setSelectedStation,
  showMockData,
}: DeploymentTabProps) {
  return (
    <div className="space-y-4">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-success/10 rounded-lg">
                <Rocket className="h-5 w-5 text-success" />
              </div>
              <div>
                <p className="text-2xl font-bold">{showMockData ? stations.filter((_, i) => getStationDeployment('', i)?.status === 'deployed').length : 0}</p>
                <p className="text-xs text-muted-foreground">Deployed</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-warning/10 rounded-lg">
                <Clock className="h-5 w-5 text-warning" />
              </div>
              <div>
                <p className="text-2xl font-bold">{showMockData ? stations.filter((_, i) => getStationDeployment('', i)?.status === 'pending').length : 0}</p>
                <p className="text-xs text-muted-foreground">Pending</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-muted rounded-lg">
                <Power className="h-5 w-5 text-muted-foreground" />
              </div>
              <div>
                <p className="text-2xl font-bold">{showMockData ? stations.filter((_, i) => !getStationDeployment('', i)).length : stations.length}</p>
                <p className="text-xs text-muted-foreground">No Model</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary/10 rounded-lg">
                <MapPin className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stations.length}</p>
                <p className="text-xs text-muted-foreground">Total Stations</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Factory Structure Tree */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Factory className="h-5 w-5" />
            Deployment by Factory Structure
          </CardTitle>
          <CardDescription>Deploy and manage AI models across your factory hierarchy</CardDescription>
        </CardHeader>
        <CardContent>
          {areas.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <MapPin className="h-10 w-10 mx-auto mb-3 opacity-50" />
              <p>No factory structure configured</p>
              <p className="text-sm">Add areas, lines, and stations in Factory Structure settings</p>
            </div>
          ) : (
            <div className="space-y-2">
              {areas.map((area) => {
                const areaLines = lines.filter(l => l.area_id === area.id);
                const isAreaExpanded = expandedAreas.has(area.id);
                
                return (
                  <div key={area.id} className="border rounded-lg overflow-hidden">
                    {/* Area Header */}
                    <button
                      onClick={() => toggleArea(area.id)}
                      className="w-full flex items-center gap-3 p-3 hover:bg-muted/50 transition-colors"
                    >
                      <div 
                        className="w-1 h-8 rounded-full" 
                        style={{ backgroundColor: area.color || "var(--primary)" }}
                      />
                      {isAreaExpanded ? (
                        <ChevronDown className="h-4 w-4 text-muted-foreground" />
                      ) : (
                        <ChevronRight className="h-4 w-4 text-muted-foreground" />
                      )}
                      <span className="font-medium">{area.name}</span>
                      <Badge variant="secondary" className="ml-auto text-xs">
                        {areaLines.length} line{areaLines.length !== 1 ? 's' : ''}
                      </Badge>
                    </button>

                    {/* Area Content - Lines */}
                    {isAreaExpanded && (
                      <div className="border-t bg-muted/20">
                        {areaLines.length === 0 ? (
                          <div className="p-4 text-sm text-muted-foreground text-center">
                            No production lines in this area
                          </div>
                        ) : (
                          areaLines.map((line) => {
                            const lineStations = stations.filter(s => s.line_id === line.id);
                            const isLineExpanded = expandedLines.has(line.id);
                            
                            return (
                              <div key={line.id} className="border-b last:border-b-0">
                                {/* Line Header */}
                                <button
                                  onClick={() => toggleLine(line.id)}
                                  className="w-full flex items-center gap-3 p-3 pl-10 hover:bg-muted/50 transition-colors"
                                >
                                  {isLineExpanded ? (
                                    <ChevronDown className="h-4 w-4 text-muted-foreground" />
                                  ) : (
                                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                                  )}
                                  <span className="font-medium text-sm">{line.name}</span>
                                  <Badge variant="outline" className="ml-auto text-xs">
                                    {lineStations.length} station{lineStations.length !== 1 ? 's' : ''}
                                  </Badge>
                                </button>

                                {/* Line Content - Stations */}
                                {isLineExpanded && (
                                  <div className="bg-background">
                                    {lineStations.length === 0 ? (
                                      <div className="p-4 pl-16 text-sm text-muted-foreground">
                                        No stations in this line
                                      </div>
                                    ) : (
                                      <div className="divide-y">
                                        {lineStations.map((station, idx) => {
                                          const deployment = showMockData ? getStationDeployment(station.name, idx) : null;
                                          const hasAIMappings = stationHasAIMappings(station.id);
                                          const mappingCount = getStationMappingCount(station.id);
                                          
                                          return (
                                            <div 
                                              key={station.id} 
                                              className="flex items-center gap-4 p-3 pl-16 hover:bg-muted/30 transition-colors"
                                            >
                                              {/* Station Info */}
                                              <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2">
                                                  <Cpu className="h-4 w-4 text-muted-foreground" />
                                                  <span className="font-medium text-sm">{station.name}</span>
                                                  {station.station_type && (
                                                    <Badge variant="secondary" className="text-xs">
                                                      {station.station_type}
                                                    </Badge>
                                                  )}
                                                  {hasAIMappings && (
                                                    <Badge variant="outline" className="text-xs text-primary border-primary/30">
                                                      AI Enabled ({mappingCount})
                                                    </Badge>
                                                  )}
                                                </div>
                                              </div>

                                              {/* Deployment Status */}
                                              {deployment ? (
                                                <>
                                                  <div className="flex items-center gap-2 min-w-[200px]">
                                                    <div className="flex-1">
                                                      <p className="text-sm font-medium">{deployment.modelName}</p>
                                                      <p className="text-xs text-muted-foreground">{deployment.version}</p>
                                                    </div>
                                                  </div>
                                                  <div className="flex items-center gap-2 min-w-[100px]">
                                                    <Progress value={deployment.accuracy} className="w-16 h-1.5" />
                                                    <span className="text-xs font-mono text-success">
                                                      {deployment.accuracy}%
                                                    </span>
                                                  </div>
                                                  <Badge 
                                                    variant={deployment.status === 'deployed' ? 'default' : 'secondary'}
                                                    className="min-w-[80px] justify-center"
                                                  >
                                                    {deployment.status === 'deployed' && <CheckCircle2 className="h-3 w-3 mr-1" />}
                                                    {deployment.status === 'pending' && <Clock className="h-3 w-3 mr-1" />}
                                                    {deployment.status}
                                                  </Badge>
                                                  {deployment.anomalies > 0 && (
                                                    <Badge variant="outline" className="text-warning border-warning/30">
                                                      <AlertTriangle className="h-3 w-3 mr-1" />
                                                      {deployment.anomalies}
                                                    </Badge>
                                                  )}
                                                  <div className="flex items-center gap-1">
                                                    {hasAIMappings && (
                                                      <Button 
                                                        variant="ghost" 
                                                        size="sm"
                                                        onClick={() => setSelectedStation(station)}
                                                      >
                                                        <Eye className="h-4 w-4" />
                                                      </Button>
                                                    )}
                                                    <Switch checked={deployment.status === 'deployed'} />
                                                    <Button variant="ghost" size="sm">
                                                      <Settings className="h-4 w-4" />
                                                    </Button>
                                                  </div>
                                                </>
                                              ) : (
                                                <>
                                                  <div className="flex-1 text-center">
                                                    <span className="text-sm text-muted-foreground">No model deployed</span>
                                                  </div>
                                                  {hasAIMappings && (
                                                    <Button 
                                                      variant="ghost" 
                                                      size="sm"
                                                      onClick={() => setSelectedStation(station)}
                                                    >
                                                      <Eye className="h-4 w-4 mr-1" />
                                                      View
                                                    </Button>
                                                  )}
                                                  <Button size="sm" variant="outline">
                                                    <Brain className="h-3 w-3 mr-1" />
                                                    Deploy Model
                                                  </Button>
                                                </>
                                              )}
                                            </div>
                                          );
                                        })}
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            );
                          })
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
